import type * as React from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlanInformationModalCloseIcon from "assets/icons/plan-information-modal-close.svg";
import GiftMembershipStepAccessIcon from "assets/images/gift-membership-step-access.svg";
import GiftMembershipStepEnvelopeIcon from "assets/images/gift-membership-step-envelope.svg";
import GiftMembershipStepPencilIcon from "assets/images/gift-membership-step-pencil.svg";
import MembershipInviteHowItWorksBlock from "components/v3/pages/account/membershipInvite/MembershipInviteHowItWorksBlock";
import type { MembershipInviteHowItWorksStep } from "components/v3/pages/account/membershipInvite/MembershipInviteHowItWorksBlock";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";

const NAVIGATION_MAILTO = "mailto:navigation@genomiclife.com";

const navigationEmailLinkSx = {
  color: "inherit",
  fontWeight: 700,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" }
} as const;

const thankYouSteps: ReadonlyArray<MembershipInviteHowItWorksStep> = [
  {
    icon: GiftMembershipStepPencilIcon,
    title: "Share the good news",
    body: "If you haven't already, let our newest member know you've signed them up."
  },
  {
    icon: GiftMembershipStepEnvelopeIcon,
    title: "Ask them to be on the lookout for an email",
    body: "Once we get their membership ready, they will receive an email with their personal invite code."
  },
  {
    icon: GiftMembershipStepAccessIcon,
    title: "Take advantage of their membership",
    body: (
      <>
        The first step is to create a portal account. If they have any trouble,
        they can email us at{" "}
        <Box
          component="a"
          href={NAVIGATION_MAILTO}
          rel="noopener noreferrer"
          sx={navigationEmailLinkSx}
        >
          navigation@genomiclife.com
        </Box>
        .
      </>
    )
  }
];

type GiftMembershipPurchaseThankYouModalProps = {
  onClose: () => void;
  open: boolean;
};

const GiftMembershipPurchaseThankYouModal: React.FC<
  GiftMembershipPurchaseThankYouModalProps
> = ({ open, onClose }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      aria-describedby="gift-purchase-thank-you-description"
      aria-labelledby="gift-purchase-thank-you-title"
      slotProps={{
        backdrop: {
          sx: {
            WebkitBackdropFilter: "blur(12px)",
            backdropFilter: "blur(12px)",
            backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.52)
          }
        }
      }}
    >
      <Box
        sx={{
          left: "50%",
          maxWidth: "750px",
          position: "absolute",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 32px)"
        }}
      >
        <Box
          sx={{
            bgcolor: COLORS.WHITE,
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
            outline: 0,
            position: "relative",
            p: { sm: 4, xs: 3 },
            pt: { sm: 5, xs: 4 },
            pr: { sm: 5, xs: 4 }
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              bgcolor: COLORS.WHITE,
              border: `1px solid ${COLORS.PRIMARY_LIGHT}`,
              boxShadow: "none",
              position: "absolute",
              right: { sm: -25, xs: 10 },
              top: { sm: -25, xs: 10 },
              "&:hover": {
                bgcolor: COLORS.LIGHT_GRAY,
                boxShadow: "none"
              }
            }}
          >
            <Box
              alt=""
              aria-hidden
              component="img"
              src={PlanInformationModalCloseIcon}
              sx={{ display: "block", height: 28, width: 28 }}
            />
          </IconButton>

          <Typography
            component="h2"
            id="gift-purchase-thank-you-title"
            variant="h4_black"
            sx={{ color: COLORS.BLACK, mb: 1.5, pr: { xs: 5, sm: 0 } }}
          >
            Thank you!
          </Typography>
          <Typography
            id="gift-purchase-thank-you-description"
            variant="body"
            sx={{ color: COLORS.DARK_GRAY_2, lineHeight: 1.6, mb: 5 }}
          >
            Within the next 72 hours, they will receive email with their invite
            code and instructions on how to sign up on their own.
          </Typography>

          <MembershipInviteHowItWorksBlock steps={thankYouSteps} />

          <Box sx={{ mt: 6 }}>
            <GLButton
              buttonType="secondary"
              onClick={onClose}
              text="Close"
              sx={{
                maxWidth: { sm: "none", xs: "100%" },
                width: { sm: "auto", xs: "100%" }
              }}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GiftMembershipPurchaseThankYouModal;
